import { useState, useEffect } from "react";
import ComponentCard from "../common/ComponentCard";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Select from "../form/Select";
import Switch from "../form/switch/Switch";
import PixelEventsField, { PixelEvents } from "./PixelEventsField";
import Button from "../ui/button/Button";
import axios from "axios";
import { authService } from "../../services/authService";
import { useNavigate } from "react-router";
import api from "../../services/api";

// Opções para plataformas de pixel
const platformOptions = [
    { value: "facebook", label: "Facebook Ads" },
    { value: "google", label: "Google Ads" },
    { value: "tiktok", label: "TikTok Ads" }
];

interface PixelFormData {
    name: string;
    platform: string;
    status: string;
    // Google Ads
    conversionId: string;
    conversionLabel: string;
    // Facebook Ads
    pixelId: string;
    accessToken: string;
    trackingDomain: string; // Domínio personalizado para tracking
    // TikTok Ads
    tiktokPixelId: string;
    // Opções de rastreamento
    trackCreditCard: boolean;
    trackPix: boolean;
    trackBoleto: boolean;
    // Configurações avançadas
    pixPercentage: number;
    boletoPercentage: number;
    enableBoletoEvent: boolean;
    companyId: string;
}

interface EditPixelFormProps {
    productId: string;
    pixelId: string;
    onSuccess: (data: any) => void;
    onError: (error: any) => void;
}

export default function EditPixelForm({ productId, pixelId, onSuccess, onError }: EditPixelFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<PixelFormData>({
        name: "",
        platform: "",
        status: "active",
        // Google Ads
        conversionId: "",
        conversionLabel: "",
        // Facebook Ads
        pixelId: "",
        accessToken: "",
        trackingDomain: "",
        // TikTok Ads
        tiktokPixelId: "",
        // Opções de rastreamento
        trackCreditCard: true,
        trackPix: true,
        trackBoleto: true,
        // Configurações avançadas
        pixPercentage: 100,
        boletoPercentage: 100,
        enableBoletoEvent: false,
        companyId: authService.getCompanyId() || ''
    });

    const [loading, setLoading] = useState(false);
    // Eventos que o pixel envia (pixel antigo sem `events` = os dois)
    const [events, setEvents] = useState<PixelEvents>({ initiateCheckout: true, purchase: true });
    // Token é só escrita: a API diz só se existe. Campo vazio = manter o salvo.
    const [hasSavedToken, setHasSavedToken] = useState(false);
    const [removeToken, setRemoveToken] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");

    // Buscar dados do pixel ao carregar o componente
    useEffect(() => {
        const fetchPixelData = async () => {
            if (!productId || !pixelId) {
                setApiError("ID do produto ou do pixel não fornecido.");
                setFetchLoading(false);
                return;
            }

            try {
                const response = await api.get(`/api/products/${productId}/pixels/${pixelId}`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.data && response.data.data.pixel) {
                    const pixel = response.data.data.pixel;

                    // Extrair ID de conversão do Google (remove o prefixo "AW-")
                    let conversionId = "";
                    if (pixel.provider === "google") {
                        // Para o Google, o ID está em conversionId, não em pixelId
                        if (pixel.conversionId) {
                            conversionId = pixel.conversionId.replace('AW-', '');
                        }
                    }

                    setFormData({
                        name: pixel.name || "",
                        platform: pixel.provider || "",
                        status: pixel.status || "active",
                        conversionId: conversionId,
                        conversionLabel: pixel.conversionLabel || "",
                        pixelId: pixel.provider === "facebook" ? pixel.pixelId || "" : "",
                        accessToken: "",
                        trackingDomain: pixel.trackingDomain || "",
                        tiktokPixelId: pixel.provider === "tiktok" ? pixel.pixelId || "" : "",
                        trackCreditCard: pixel.trackingOptions?.creditCard || false,
                        trackPix: pixel.trackingOptions?.pix || false,
                        trackBoleto: pixel.trackingOptions?.billet || false,
                        // Ler de eventConfig (nova estrutura da API)
                        pixPercentage: pixel.eventConfig?.pixPercentage ?? pixel.pixPercentage ?? 100,
                        boletoPercentage: pixel.eventConfig?.boletoPercentage ?? pixel.boletoPercentage ?? 100,
                        enableBoletoEvent: pixel.eventConfig?.enableBoletoEvent ?? pixel.enableBoletoEvent ?? false,
                        companyId: pixel.companyId || authService.getCompanyId() || ""
                    });
                    setHasSavedToken(!!pixel.hasAccessToken);
                    setEvents({
                        initiateCheckout: pixel.events?.initiateCheckout !== false,
                        purchase: pixel.events?.purchase !== false,
                    });
                }
            } catch (error: any) {
                setApiError(error.response?.data?.message || "Erro ao carregar dados do pixel");
            } finally {
                setFetchLoading(false);
            }
        };

        fetchPixelData();
    }, [productId, pixelId]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { id, value } = e.target;

        // Limpar o erro específico quando o campo é modificado
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Mapeamento de IDs para estrutura do formData
        switch (id) {
            case "inputName":
                setFormData({ ...formData, name: value });
                break;
            case "conversionId":
                setFormData({ ...formData, conversionId: value });
                break;
            case "conversionLabel":
                setFormData({ ...formData, conversionLabel: value });
                break;
            case "pixelId":
                setFormData({ ...formData, pixelId: value });
                break;
            case "accessToken":
                setFormData({ ...formData, accessToken: value });
                break;
            case "tiktokPixelId":
                setFormData({ ...formData, tiktokPixelId: value });
                break;
            case "trackingDomain":
                setFormData({ ...formData, trackingDomain: value });
                break;
            case "pixPercentage":
                setFormData({ ...formData, pixPercentage: value === '' ? 100 : Math.min(100, Math.max(0, Number(value) || 0)) });
                break;
            case "boletoPercentage":
                setFormData({ ...formData, boletoPercentage: value === '' ? 100 : Math.min(100, Math.max(0, Number(value) || 0)) });
                break;
            default:
                break;
        }
    };

    const handlePlatformChange = (value: string) => {
        setFormData({ ...formData, platform: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.platform && value) {
            setErrors({
                ...errors,
                platform: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleSwitchChange = (checked: boolean) => {
        setFormData({
            ...formData,
            status: checked ? "active" : "inactive"
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");
    };

    const handleBoletoEventChange = (checked: boolean) => {
        setFormData({
            ...formData,
            enableBoletoEvent: checked
        });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name) {
            newErrors.inputName = "O nome do pixel é obrigatório";
        }

        if (!formData.platform) {
            newErrors.platform = "A plataforma é obrigatória";
        }

        if (!events.initiateCheckout && !events.purchase) {
            newErrors.events = "Selecione pelo menos um evento";
        }

        // Validação específica para Google Ads
        if (formData.platform === "google") {
            if (!formData.conversionId) {
                newErrors.conversionId = "ID de conversão é obrigatório";
            } else if (!/^[0-9]+$/.test(formData.conversionId)) {
                newErrors.conversionId = "ID de conversão deve conter apenas números";
            }

            if (!formData.conversionLabel) {
                newErrors.conversionLabel = "Rótulo de conversão é obrigatório";
            }
        }

        // Validação específica para Facebook Ads
        if (formData.platform === "facebook") {
            if (!formData.pixelId) {
                newErrors.pixelId = "ID do pixel é obrigatório";
            }
        }

        // Validação específica para TikTok Ads
        if (formData.platform === "tiktok") {
            if (!formData.tiktokPixelId) {
                newErrors.tiktokPixelId = "ID do pixel é obrigatório";
            }
        }

        setErrors(newErrors);

        // Se não houver erros, retorna true
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        // Verificar se o productId e pixelId estão definidos
        if (!productId) {
            setApiError("ID do produto não fornecido. Não é possível atualizar o pixel.");
            return;
        }

        if (!pixelId) {
            setApiError("ID do pixel não fornecido. Não é possível atualizar o pixel.");
            return;
        }

        setLoading(true);

        try {
            // Preparar dados para API
            const pixelData = {
                name: formData.name,
                provider: formData.platform,
                pixelId: formData.platform === 'google' ? `AW-${formData.conversionId}` :
                    formData.platform === 'facebook' ? formData.pixelId :
                        formData.tiktokPixelId,
                conversionLabel: formData.platform === 'google' ? formData.conversionLabel : undefined,
                // Vazio = manter o token salvo (o backend nunca devolve o token)
                accessToken: formData.platform === 'facebook' && formData.accessToken.trim() ? formData.accessToken.trim() : undefined,
                removeAccessToken: formData.platform === 'facebook' && removeToken && !formData.accessToken.trim() ? true : undefined,
                events,
                creditCard: formData.trackCreditCard,
                pix: formData.trackPix,
                billet: formData.trackBoleto,
                pixPercentage: formData.pixPercentage,
                boletoPercentage: formData.boletoPercentage,
                enableBoletoEvent: formData.enableBoletoEvent,
                status: formData.status,
                companyId: formData.companyId
            };

            // Chamada para a API usando PUT
            const response = await axios.put(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}/pixels/${pixelId}`,
                pixelData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.status === 200) {
                setApiSuccess("Pixel atualizado com sucesso!");

                // Chamar o callback de sucesso
                onSuccess(response.data);

                // Redirecionar após 1 segundo
                setTimeout(() => {
                    navigate(`/products/edit/${productId}?tab=pixel`);
                }, 1000);
            }
        } catch (error: any) {
            setApiError(error.response?.data?.message || "Erro ao atualizar pixel");

            // Chamar o callback de erro
            onError(error);
        } finally {
            setLoading(false);
        }
    };

    if (fetchLoading) {
        return <div className="p-4 text-center">Carregando dados do pixel...</div>;
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Editar Pixel">
                    {apiError && (
                        <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                            {apiError}
                        </div>
                    )}

                    {apiSuccess && (
                        <div className="bg-green-50 dark:bg-green-900/10 text-green-600 dark:text-green-400 p-3 rounded-lg mb-4">
                            {apiSuccess}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="mt-5">
                            <Label>Plataforma *</Label>
                            <Select
                                options={platformOptions}
                                placeholder="Selecione uma plataforma"
                                onChange={handlePlatformChange}
                                value={formData.platform}
                                className={`dark:bg-dark-900 ${errors.platform ? 'border-red-500' : ''}`}
                            />
                            {errors.platform && <p className="mt-1 text-sm text-red-500">{errors.platform}</p>}
                        </div>

                        {formData.platform && (
                            <div className="mt-5">
                                <Switch
                                    label="Ativar Pixel"
                                    checked={formData.status === "active"}
                                    onChange={handleSwitchChange}
                                />
                            </div>
                        )}

                        {formData.platform && (
                            <div className="mt-5">
                                <Label htmlFor="inputName">Nome do Pixel *</Label>
                                <Input
                                    type="text"
                                    id="inputName"
                                    placeholder="Informe o nome do pixel"
                                    onChange={handleInputChange}
                                    value={formData.name}
                                    error={!!errors.inputName}
                                    hint={errors.inputName}
                                />
                            </div>
                        )}

                        {formData.platform === "google" && (
                            <>
                                <div className="mt-5">
                                    <Label htmlFor="conversionId">ID de conversão *</Label>
                                    <div className="relative">
                                        <Input
                                            type="text"
                                            id="conversionId"
                                            placeholder="Ex: 123456789"
                                            onChange={handleInputChange}
                                            value={formData.conversionId}
                                            error={!!errors.conversionId}
                                            hint={errors.conversionId}
                                            className="pl-[62px]"
                                        />
                                        <span className="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-gray-500 dark:border-gray-800 dark:text-gray-400 z-10">
                                            AW-
                                        </span>
                                    </div>
                                </div>
                                <div className="mt-5">
                                    <Label htmlFor="conversionLabel">Rótulo de conversão *</Label>
                                    <Input
                                        type="text"
                                        id="conversionLabel"
                                        placeholder="Ex: abcDEF123"
                                        onChange={handleInputChange}
                                        value={formData.conversionLabel}
                                        error={!!errors.conversionLabel}
                                        hint={errors.conversionLabel}
                                    />
                                </div>
                            </>
                        )}

                        {formData.platform === "facebook" && (
                            <>
                                <div className="mt-5">
                                    <Label htmlFor="pixelId">ID do pixel *</Label>
                                    <Input
                                        type="text"
                                        id="pixelId"
                                        placeholder="Ex: 123456789012345"
                                        onChange={handleInputChange}
                                        value={formData.pixelId}
                                        error={!!errors.pixelId}
                                        hint={errors.pixelId}
                                    />
                                </div>
                                <div className="mt-5">
                                    <Label htmlFor="accessToken">Token de acesso (opcional)</Label>
                                    <Input
                                        type="text"
                                        id="accessToken"
                                        placeholder={hasSavedToken && !removeToken
                                            ? "Token salvo. Deixe em branco para manter, ou cole um novo para trocar"
                                            : "Token de acesso para API de Conversões"}
                                        onChange={handleInputChange}
                                        value={formData.accessToken}
                                    />
                                    {hasSavedToken && (
                                        <p className="mt-1 text-xs">
                                            {removeToken ? (
                                                <>
                                                    <span className="text-amber-600 dark:text-amber-400">O token será removido ao salvar: os eventos voltam a sair só pelo navegador. </span>
                                                    <button type="button" className="text-brand-500 underline" onClick={() => setRemoveToken(false)}>Desfazer</button>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="text-green-600 dark:text-green-400">✓ Token salvo. Por segurança, ele não é exibido. </span>
                                                    <button type="button" className="text-error-500 underline" onClick={() => setRemoveToken(true)}>Remover token</button>
                                                </>
                                            )}
                                        </p>
                                    )}
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Com o token, os eventos também saem pelo servidor (API de Conversões), com o mesmo ID do navegador: o Facebook conta uma vez e não perde venda por bloqueador. Sem token, só pelo navegador.
                                    </p>
                                </div>
                            </>
                        )}

                        {formData.platform === "tiktok" && (
                            <div className="mt-5">
                                <Label htmlFor="tiktokPixelId">ID do pixel *</Label>
                                <Input
                                    type="text"
                                    id="tiktokPixelId"
                                    placeholder="Ex: ABCDEF123456"
                                    onChange={handleInputChange}
                                    value={formData.tiktokPixelId}
                                    error={!!errors.tiktokPixelId}
                                    hint={errors.tiktokPixelId}
                                />
                            </div>
                        )}

                        {formData.platform && (
                            <PixelEventsField
                                value={events}
                                onChange={value => {
                                    setEvents(value);
                                    if (errors.events) setErrors({ ...errors, events: "" });
                                }}
                                error={errors.events}
                            />
                        )}

                        {formData.platform && (
                            <>
                                <div className="mt-6 border-t border-gray-200 dark:border-gray-800 pt-6">
                                    <h3 className="text-sm font-medium text-gray-800 dark:text-white/90 mb-4">
                                        Configurações Avançadas
                                    </h3>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <Label htmlFor="pixPercentage">Porcentagem do valor PIX (%)</Label>
                                            <Input
                                                type="number"
                                                id="pixPercentage"
                                                placeholder="100"
                                                min="0"
                                                max="100"
                                                onChange={handleInputChange}
                                                value={formData.pixPercentage}
                                            />
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                Porcentagem do valor a ser enviada no evento Purchase para PIX
                                            </p>
                                        </div>
                                        
                                        <div>
                                            <Label htmlFor="boletoPercentage">Porcentagem do valor Boleto (%)</Label>
                                            <Input
                                                type="number"
                                                id="boletoPercentage"
                                                placeholder="100"
                                                min="0"
                                                max="100"
                                                onChange={handleInputChange}
                                                value={formData.boletoPercentage}
                                            />
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                Porcentagem do valor a ser enviada no evento Purchase para Boleto
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-5">
                                        <Switch
                                            label="Disparar evento Purchase para boleto gerado"
                                            checked={formData.enableBoletoEvent}
                                            onChange={handleBoletoEventChange}
                                        />
                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            Por padrão, apenas o evento "boleto" é disparado. Ative para também disparar "Purchase"
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}

                        {formData.platform && (
                            <div className="mt-6 flex justify-end gap-4.5">
                                <Button
                                    size="md"
                                    variant="outline"
                                    onClick={() => navigate(`/products/edit/${productId}?tab=pixel`)}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    size="md"
                                    variant="primary"
                                    onClick={() => handleSubmit()}
                                    disabled={loading}
                                >
                                    {loading ? "Salvando..." : "Atualizar Pixel"}
                                </Button>
                            </div>
                        )}
                    </form>
                </ComponentCard>
            </div>
        </div>
    );
}
